/**
 * Typed fetch wrapper for admin-backend. Goes through Vite's /api proxy in
 * dev so cookies stay first-party. All requests include credentials so the
 * session cookie travels both ways.
 *
 * Path-prefix: when the app is mounted at /jobs/, import.meta.env.BASE_URL
 * is '/jobs/'. We need API calls to land at '/jobs/api/...', not '/api/...'.
 */
const BASE_PREFIX = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
const BASE = `${BASE_PREFIX}/api`;

class ApiError extends Error {
  status: number;
  details: unknown;
  constructor(status: number, message: string, details: unknown) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

async function call<T = unknown>(
  method: string,
  path: string,
  init: { body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  let body: BodyInit | undefined;
  if (init.body !== undefined && !(init.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(init.body);
  } else if (init.body instanceof FormData) {
    body = init.body;
  }
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers,
    body,
    signal: init.signal,
  });
  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }
  if (!res.ok) {
    const err = parsed as { error?: string } | null;
    throw new ApiError(
      res.status,
      err?.error ?? `${method} ${path} failed (${res.status})`,
      parsed,
    );
  }
  return parsed as T;
}

export const api = {
  get: <T = unknown>(path: string, signal?: AbortSignal) =>
    call<T>('GET', path, { signal }),
  post: <T = unknown>(path: string, body?: unknown, signal?: AbortSignal) =>
    call<T>('POST', path, { body, signal }),
  patch: <T = unknown>(path: string, body?: unknown) => call<T>('PATCH', path, { body }),
  put: <T = unknown>(path: string, body?: unknown) => call<T>('PUT', path, { body }),
  delete: <T = unknown>(path: string) => call<T>('DELETE', path),
  postForm: <T = unknown>(path: string, formData: FormData) =>
    call<T>('POST', path, { body: formData }),
  ApiError,
};

export { ApiError };
